import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  ClipboardCheck, Eye, ChevronLeft, ChevronRight,
  UserCheck, UserX, Wifi, WifiOff,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, AppButton, AppCard, AppCardContent,
  EmptyState,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./attendance.scss";

/**
 * InstructorAttendancePage — Attendance view for instructors.
 * Shows sessions only for batches assigned to the instructor.
 * Allows marking offline attendance when enabled.
 *
 * Route: /i/attendance
 */
const InstructorAttendancePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Fetch instructor's batches and offline setting
  useEffect(() => {
    const fetchInit = async () => {
      try {
        const [batchRes, offlineRes] = await Promise.all([
          apiClient.get("/batches/list?limit=100&fields=name"),
          apiClient.get("/attendance/check-offline-enabled").catch(() => ({ data: {} })),
        ]);
        setBatches(batchRes.data.batches || []);
        setOfflineEnabled(offlineRes.data.offlineAttendanceEnabled === true);
      } catch { /* ignore */ }
    };
    fetchInit();
  }, []);

  // Fetch sessions for selected batch
  const fetchSessions = useCallback(async () => {
    if (!selectedBatch) {
      setSessions([]);
      setTotal(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.get(
        `/attendance/batch/${selectedBatch}?page=${page}&limit=15`
      );
      setSessions(data.sessions || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch {
      setToast({ show: true, message: "Failed to load attendance data.", type: "danger" });
    }
    setLoading(false);
  }, [selectedBatch, page]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit",
    });
  };

  const getSessionSummary = (session) => {
    const participants = session.participants || [];
    const present = participants.filter((p) => p.attendanceStatus === "present").length;
    const absent = participants.filter((p) => p.attendanceStatus === "absent").length;
    const online = participants.filter((p) => p.source === "online").length;
    const manual = participants.filter((p) => p.source === "manual").length;
    const durationMins = session.sessionEnd && session.sessionStart
      ? Math.round((new Date(session.sessionEnd) - new Date(session.sessionStart)) / 60000) : 0;
    return { present, absent, online, manual, total: participants.length, durationMins };
  };

  return (
    <div className="attendance-page sl-page">
      <PageHeader
        icon={ClipboardCheck}
        title="Attendance"
        description="View and manage attendance for your batches"
        actions={
          offlineEnabled ? (
            <AppButton variant="primary" size="sm" onClick={() => navigate("/i/attendance/offline")}>
              <WifiOff size={16} /> Mark Offline Attendance
            </AppButton>
          ) : null
        }
      />

      {/* Batch Selector */}
      <AppCard className="mb-3">
        <AppCardContent>
          <div className="d-flex align-items-center gap-3">
            <label className="fw-medium text-nowrap" style={{ fontSize: "0.85rem" }}>Select Batch:</label>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 350 }}
              value={selectedBatch}
              onChange={(e) => { setSelectedBatch(e.target.value); setPage(1); }}
            >
              <option value="">— Choose a batch —</option>
              {batches.map((b) => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
          </div>
        </AppCardContent>
      </AppCard>

      {!selectedBatch ? (
        <EmptyState
          icon={<ClipboardCheck size={40} />}
          title="Select a batch"
          description="Choose a batch above to view its attendance sessions."
        />
      ) : loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : sessions.length === 0 ? (
        <EmptyState
          icon={<ClipboardCheck size={40} />}
          title="No attendance records"
          description="No attendance sessions found for this batch yet."
        />
      ) : (
        <>
          <div className="attendance-table-container">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Duration</th>
                  <th><Wifi size={14} /> Online</th>
                  <th><WifiOff size={14} /> Offline</th>
                  <th><UserCheck size={14} /> Present</th>
                  <th><UserX size={14} /> Absent</th>
                  <th>Manual</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const summary = getSessionSummary(s);
                  return (
                    <tr key={s._id}>
                      <td>
                        <div className="attendance-date-cell">
                          <span className="attendance-date-cell__date">{formatDate(s.sessionDate)}</span>
                          <span className="attendance-date-cell__time">
                            {formatTime(s.sessionStart)} – {formatTime(s.sessionEnd)}
                          </span>
                        </div>
                      </td>
                      <td>{summary.durationMins} min</td>
                      <td>{summary.online}</td>
                      <td>{summary.manual}</td>
                      <td className="attendance-cell--present">{summary.present}</td>
                      <td className="attendance-cell--absent">{summary.absent}</td>
                      <td>
                        {s.manualAttendanceCompleted ? (
                          <span className="attendance-badge attendance-badge--done">Done</span>
                        ) : (
                          <span className="attendance-badge attendance-badge--pending">Pending</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="attendance-action-btn"
                          title="View Details"
                          onClick={() => navigate(`/i/attendance/session/${s._id}`)}
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="attendance-pagination">
              <span className="attendance-pagination__info">
                Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, total)} of {total}
              </span>
              <div className="attendance-pagination__controls">
                <AppButton
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={14} /> Prev
                </AppButton>
                <span className="attendance-pagination__page">
                  Page {page} of {totalPages}
                </span>
                <AppButton
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next <ChevronRight size={14} />
                </AppButton>
              </div>
            </div>
          )}
        </>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default InstructorAttendancePage;
